import { TextStats } from '../types';

export const calculateTextStats = (text: string): TextStats => {
  const charsWithSpaces = text.length;
  const charsWithoutSpaces = text.replace(/\s/g, '').length;
  const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim().length > 0).length;
  const readingTime = Math.ceil(words / 200); // 분당 200단어 기준

  return {
    charsWithSpaces,
    charsWithoutSpaces,
    words,
    sentences,
    paragraphs,
    readingTime,
  };
};

export const convertCase = {
  toUpperCase: (text: string) => text.toUpperCase(),
  toLowerCase: (text: string) => text.toLowerCase(),
  toTitleCase: (text: string) =>
    text.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()),
  toSentenceCase: (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1).toLowerCase(),
  toggleCase: (text: string) =>
    text
      .split('')
      .map((char) => (char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()))
      .join(''),
};

export const trimWhitespace = {
  trimStartEnd: (text: string) => text.trim(),
  removeAll: (text: string) => text.replace(/\s/g, ''),
  removeMultiple: (text: string) => text.replace(/\s+/g, ' '),
  removeNewlines: (text: string) => text.replace(/\n/g, ''),
};

export const sortLines = {
  ascending: (text: string) =>
    text.split('\n').sort((a, b) => a.localeCompare(b)).join('\n'),
  descending: (text: string) =>
    text.split('\n').sort((a, b) => b.localeCompare(a)).join('\n'),
  reverse: (text: string) => text.split('\n').reverse().join('\n'),
  shuffle: (text: string) => {
    const lines = text.split('\n');
    for (let i = lines.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [lines[i], lines[j]] = [lines[j], lines[i]];
    }
    return lines.join('\n');
  },
};

export const removeDuplicates = {
  lines: (text: string, caseSensitive = true) => {
    const lines = text.split('\n');
    const unique = caseSensitive
      ? Array.from(new Set(lines))
      : Array.from(new Set(lines.map((line) => line.toLowerCase()))).map((line) =>
          lines.find((l) => l.toLowerCase() === line)
        );
    return unique.filter(Boolean).join('\n');
  },
  words: (text: string, caseSensitive = true) => {
    const words = text.split(/\s+/);
    const unique = caseSensitive
      ? Array.from(new Set(words))
      : Array.from(new Set(words.map((word) => word.toLowerCase()))).map((word) =>
          words.find((w) => w.toLowerCase() === word)
        );
    return unique.filter(Boolean).join(' ');
  },
};

export const removeCharacters = {
  specialChars: (text: string) => text.replace(/[^\w\s가-힣]/g, ''),
  emoji: (text: string) =>
    text.replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu,
      ''
    ),
  numbers: (text: string) => text.replace(/\d/g, ''),
  english: (text: string) => text.replace(/[a-zA-Z]/g, ''),
  korean: (text: string) => text.replace(/[가-힣]/g, ''),
};

export const base64 = {
  encode: (text: string) => {
    try {
      return btoa(unescape(encodeURIComponent(text)));
    } catch (e) {
      return 'Error: Invalid input';
    }
  },
  decode: (text: string) => {
    try {
      return decodeURIComponent(escape(atob(text)));
    } catch (e) {
      return 'Error: Invalid Base64 string';
    }
  },
};

export const generateLoremIpsum = (paragraphs: number): string => {
  const lorem = [
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
    'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
    'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
    'Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.',
  ];

  return Array(paragraphs)
    .fill(0)
    .map((_, i) => lorem[i % lorem.length])
    .join('\n\n');
};

// 한영 변환 맵
const koreanMap: { [key: string]: string } = {
  q: 'ㅂ', w: 'ㅈ', e: 'ㄷ', r: 'ㄱ', t: 'ㅅ', y: 'ㅛ', u: 'ㅕ', i: 'ㅑ', o: 'ㅐ', p: 'ㅔ',
  a: 'ㅁ', s: 'ㄴ', d: 'ㅇ', f: 'ㄹ', g: 'ㅎ', h: 'ㅗ', j: 'ㅓ', k: 'ㅏ', l: 'ㅣ',
  z: 'ㅋ', x: 'ㅌ', c: 'ㅊ', v: 'ㅍ', b: 'ㅠ', n: 'ㅜ', m: 'ㅡ',
  Q: 'ㅃ', W: 'ㅉ', E: 'ㄸ', R: 'ㄲ', T: 'ㅆ', O: 'ㅒ', P: 'ㅖ',
};

export const convertEnglishToKorean = (text: string): string => {
  // 간단한 자음 변환만 (완전한 한글 조합은 hangul-js 라이브러리 사용 권장)
  return text
    .split('')
    .map((char) => koreanMap[char] || char)
    .join('');
};
